
/**
 * Admin help copy, keyed by ROUTE rather than by title.
 *
 * Same discipline `help-copy-trfer.ts` established (#611): the copy is keyed
 * to `admin-nav.ts`'s own hrefs, so `HelpPage.navParity.test.tsx` fails the
 * build the moment a nav row and its help text disagree, in either direction.
 *
 * Cut to the System / Oversee / Configure shape in the 2026-09-20
 * admin-section subtraction (`admin-nav.ts`'s own docstring has the full
 * rationale): every entry that mirrored a TRFer-desk page — signups,
 * applications, grant requests, forms, polls, partner hospitals,
 * the old cached Agent status — left with its nav row, because the reader
 * now reaches the same page through the sidebar's role switcher instead of a
 * second, admin-shell address for it.
 *
 * Admin copy names no family persona: it spans the whole platform, and
 * families pick their own (Maya by default). Agents are named by their
 * internal names, as System Health lists them (#3066).
 *
 * Every claim was checked against the page it documents on 2026-08-29 — the
 * page's own lede, not the previous help text — except `/admin/users` and the
 * two System entries, rechecked 2026-09-20 against the post-subtraction page.
 */
export const ADMIN_HELP_COPY: Record<string, string> = {
  '/admin':
    'Platform-wide view: active users, recent activity, and system health. Your starting point for anything that needs admin attention.',

  // ── System ──
  '/admin/health':
    `Process uptime, memory and CPU, and a live probe of MongoDB, Neo4j, Redis, the legal-document integrity check, and each of the four AI agents the backend calls (Clio, Guardian, FinanceBot, Williams). Refreshes every 15 seconds.`,
  '/admin/stats':
    'Platform totals for users, families and tasks, the browser errors families’ devices reported in the last 7 days, users by role, the success metrics against their targets, and how many messages each agent handled today and in total.',
  '/admin/translation-reports':
    'Bad-translation reports from parents and TRFers, so wording that reads wrong in another language reaches someone who can correct it.',

  // ── Oversee ──
  '/admin/reviews':
    `Rate the assistant’s replies, message by message, to check its quality. A family appears here when they let TRF’s program leads read their conversations.`,
  '/admin/conversations':
    'Read a family’s conversations with their companion, exactly as they happened. A family appears here when they turned on “Let TRF’s program leads read your conversations” in their Settings. TRFers can’t read them, and neither can William. The engineers who build the app may still see a conversation while fixing a problem, and the family’s Settings say so. Every conversation you open is recorded in the audit log with your name and the time. Nothing here is rated or changed.',
  '/admin/handoffs':
    'Read-only view of every open conversation currently in TRFer hands, across the whole platform. Watching, not intervening — the counselor holding the conversation keeps it. Live conversations are switched off for launch, so this view stays empty until they are turned back on.',
  '/admin/feedback':
    'Bug reports, ideas, and praise submitted from every role, with the page and the account each one came from.',

  // ── Configure ──
  '/admin/users':
    'Access control: the account and role surface only an admin has. Create an account, grant or remove a role — including admin — toggle Lead TRFer, reset a password, hard-delete, and read the audit log of who changed what. You can also impersonate any user to debug from their perspective, with the menus you grant for that session. Who holds which family is on Families. The day-to-day roster, including approving a pending signup, is the TRFer desk’s Users page.',
  '/admin/settings':
    'One switch: close applications to Canadian families. It saves the moment you flip it, and a Canadian family with a draft application or renewal cannot submit while it is on. The LLM configuration below is read-only — change the model in the backend config, not here.',
  '/admin/handoff-config':
    'Staffing mode, office hours, SLA windows, and per-signal routing for in-thread TRFer handoffs — org-wide operational configuration, not a per-TRFer preference. The same form also appears embedded in the TRFer Settings Handoff tab; changes made either place are the same saved configuration.',
  '/admin/legal':
    'Publish a new version of a legal document. Preview the blast radius before you confirm — a material change blocks affected users into re-consent the next time they sign in.',
  '/admin/bill-links':
    'Map TRF families and people onto their Bill.com Spend & Expense budgets and users. A mapping belongs to one environment: a sandbox ID can never be used against production. The live Bill.com connection is coming soon — until TRF’s production Bill.com token is issued, nothing here moves real money.',

  // ── Footer tray ──
  '/settings':
    'Your own preferences: notification choices, appearance, the companion persona, and account security including two-factor authentication.',
};
